import type { QueryFunction, UseQueryOptions, QueryClient } from 'react-query'
import type {
	CoworkingSolutionGroup,
	CoworkingSolution,
	SortedCoworkingSolutionWithExtraInfo,
} from '@alexanderathoodly/data-models'
import { apiClient } from '~/utils/requests'
import type { AuthQueryFactory } from './useauthquery'

export type SolutionCriteria = {
	min_workspaces?: number | undefined
	max_workspaces?: number | undefined
	budget?: number | undefined
	areas?: string[]
	location?: string | undefined
	services?: string[]
	types?: string[] | undefined
	area_url?: string | undefined
	page?: number | undefined
}

type SolutionsByLocationQueryKey = [
	'coworking_solutions',
	{ locationId?: string }
]

const EMPTY_SORTED_COWORKING_SOLUTION_WITH_EXTRA_INFO = {
	sorted_solutions: [],
	min_solution_price: 0,
	min_workspaces: 0,
	max_workspaces: 0,
}

const fetchSolutionsByLocation: QueryFunction<
	SortedCoworkingSolutionWithExtraInfo,
	SolutionsByLocationQueryKey
> = async ({ queryKey }) => {
	const { locationId } = queryKey[1]
	if (!locationId) {
		return EMPTY_SORTED_COWORKING_SOLUTION_WITH_EXTRA_INFO
	}
	return await apiClient.getCoworkingSolutionsByLocationId(locationId)
}

export const getCoworkingSolutionsByLocationQuery: (
	locationId?: string
) => UseQueryOptions<
	SortedCoworkingSolutionWithExtraInfo,
	Error,
	SortedCoworkingSolutionWithExtraInfo,
	SolutionsByLocationQueryKey
> = (locationId) => {
	return {
		queryKey: ['coworking_solutions', { locationId: locationId || '' }],
		queryFn: fetchSolutionsByLocation,
		staleTime: 15 * 60 * 1000, // Valid 15 minutes
		enabled: locationId != undefined,
	}
}

type AuthSolutionsByLocationQueryKey = [
	'coworking_solutions',
	'auth',
	{ locationId?: string }
]

export const getAuthCoworkingSolutionsByLocationQuery: AuthQueryFactory<
	[locationId?: string],
	SortedCoworkingSolutionWithExtraInfo,
	SortedCoworkingSolutionWithExtraInfo,
	AuthSolutionsByLocationQueryKey
> = (_, locationId) => {
	return {
		queryKey: ['coworking_solutions', 'auth', { locationId: locationId || '' }],
		queryFn: async ({ jwtToken, queryKey }) => {
			const locationId = queryKey[2].locationId
			if (!locationId) {
				return EMPTY_SORTED_COWORKING_SOLUTION_WITH_EXTRA_INFO
			}
			return await apiClient.getAuthCoworkingSolutionsByLocationId(
				locationId,
				jwtToken
			)
		},
		staleTime: 15 * 60 * 1000, // Valid 15 minutes
		enabled: locationId != undefined,
	}
}

type AISolutionsByLocationQueryKey = [
	'coworking_solutions',
	'AI',
	{ locationId?: string }
]

const fetchAISolutionsByLocation: QueryFunction<
	SortedCoworkingSolutionWithExtraInfo,
	AISolutionsByLocationQueryKey
> = async ({ queryKey }) => {
	const { locationId } = queryKey[2]
	if (!locationId) {
		return EMPTY_SORTED_COWORKING_SOLUTION_WITH_EXTRA_INFO
	}
	return await apiClient.getAICoworkingSolutionsByLocationId(locationId)
}

export const getAICoworkingSolutionsByLocationQuery: (
	locationId?: string
) => UseQueryOptions<
	SortedCoworkingSolutionWithExtraInfo,
	Error,
	SortedCoworkingSolutionWithExtraInfo,
	AISolutionsByLocationQueryKey
> = (locationId) => {
	return {
		queryKey: ['coworking_solutions', 'AI', { locationId: locationId || '' }],
		queryFn: fetchAISolutionsByLocation,
		staleTime: 15 * 60 * 1000, // Valid 15 minutes
		enabled: locationId != undefined,
	}
}

type SolutionQueryKey = ['coworking_solution', { id: string }]

const fetchSolution: QueryFunction<
	CoworkingSolution,
	SolutionQueryKey
> = async ({ queryKey }) => {
	return await apiClient.getCoworkingSolutionById(queryKey[1].id)
}

export const getCoworkingSolutionQuery: (
	id: string,
	queryClient: QueryClient
) => UseQueryOptions<
	CoworkingSolution,
	Error,
	CoworkingSolution,
	SolutionQueryKey
> = (id, queryClient) => {
	return {
		queryKey: ['coworking_solution', { id }],
		queryFn: fetchSolution,
		staleTime: 15 * 60 * 1000, // Valid 15 minutes
		enabled: !!id,
		initialData: () => {
			const groups = queryClient.getQueryData<CoworkingSolutionGroup[]>(
				'coworking_solutions_groups',
				{
					exact: false,
				}
			)
			if (groups === undefined) {
				return undefined
			}
			for (const group of groups) {
				for (const solution of group.solutions.results) {
					if (solution.id === id) {
						return solution
					}
				}
			}
			return undefined
		},
	}
}
